import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/RetroLoveSnake.css';

const GRID_SIZE = 16;
const INITIAL_SNAKE = [
  { x: 8, y: 8 },
  { x: 8, y: 9 },
  { x: 8, y: 10 },
];

function drawRoundedRect(ctx, x, y, w, h, r) {
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.rect(x, y, w, h);
  }
}

const RetroLoveSnake = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('love_snake_highscore') || '0', 10);
  });
  const [isGameOver, setIsGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // High performance game state refs (avoids React re-render lag)
  const snakeRef = useRef(INITIAL_SNAKE);
  const dirRef = useRef({ x: 0, y: -1 });
  const inputQueueRef = useRef([]); // FIFO input buffer to prevent dropped turns
  const foodRef = useRef({ x: 8, y: 4 });
  const scoreRef = useRef(0);
  const isGameOverRef = useRef(false);
  const isPausedRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Sync state refs
  scoreRef.current = score;
  isGameOverRef.current = isGameOver;
  isPausedRef.current = isPaused;

  const getRandomFood = useCallback((currentSnake) => {
    let newPos;
    let collision = true;
    while (collision) {
      newPos = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      };
      // eslint-disable-next-line no-loop-func
      collision = currentSnake.some((seg) => seg.x === newPos.x && seg.y === newPos.y);
    }
    return newPos;
  }, []);

  const resetGame = useCallback(() => {
    snakeRef.current = INITIAL_SNAKE;
    dirRef.current = { x: 0, y: -1 };
    inputQueueRef.current = [];
    const newFood = getRandomFood(INITIAL_SNAKE);
    foodRef.current = newFood;
    setScore(0);
    scoreRef.current = 0;
    setIsGameOver(false);
    isGameOverRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
  }, [getRandomFood]);

  // Fast, responsive direction queueing (prevents 180 reversals & missed turns)
  const queueDirection = useCallback((newDir) => {
    const lastDir = inputQueueRef.current.length > 0
      ? inputQueueRef.current[inputQueueRef.current.length - 1]
      : dirRef.current;

    // Disallow 180-degree direct reversal into itself
    if (newDir.x === -lastDir.x && newDir.y === -lastDir.y) return;
    // Disallow duplicate direction
    if (newDir.x === lastDir.x && newDir.y === lastDir.y) return;

    // Buffer up to 2 queued turns for lightning-fast responsive controls
    if (inputQueueRef.current.length < 2) {
      inputQueueRef.current.push(newDir);
    }
  }, []);

  // Keyboard controls with zero latency
  useEffect(() => {
    const handleKey = (e) => {
      if (isGameOverRef.current) {
        if (e.key === ' ' || e.key === 'Enter') resetGame();
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          queueDirection({ x: 0, y: -1 });
          e.preventDefault();
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          queueDirection({ x: 0, y: 1 });
          e.preventDefault();
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          queueDirection({ x: -1, y: 0 });
          e.preventDefault();
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          queueDirection({ x: 1, y: 0 });
          e.preventDefault();
          break;
        case ' ':
          setIsPaused((p) => !p);
          e.preventDefault();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [resetGame, queueDirection]);

  // Smooth Game Loop with requestAnimationFrame & Low MS Tick
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let animId;
    let lastTickTime = performance.now();

    const gameLoop = (time) => {
      animId = requestAnimationFrame(gameLoop);

      // Balanced, comfortable speed: starts at 135ms, gradually speeds up to 95ms
      const curScore = scoreRef.current;
      const tickSpeed = Math.max(95, 135 - Math.floor(curScore / 40) * 3);

      if (!isGameOverRef.current && !isPausedRef.current) {
        if (time - lastTickTime >= tickSpeed) {
          lastTickTime = time;

          // 1. Advance direction from FIFO queue
          if (inputQueueRef.current.length > 0) {
            dirRef.current = inputQueueRef.current.shift();
          }
          const curDir = dirRef.current;
          const prevSnake = snakeRef.current;
          const head = prevSnake[0];

          // 2. Wall wrap-around (duvardan serbestçe geçiş)
          const nextHead = {
            x: (head.x + curDir.x + GRID_SIZE) % GRID_SIZE,
            y: (head.y + curDir.y + GRID_SIZE) % GRID_SIZE,
          };

          // 3. Sadece kendine çarpınca yanma (Self collision only)
          if (prevSnake.some((seg) => seg.x === nextHead.x && seg.y === nextHead.y)) {
            setIsGameOver(true);
            isGameOverRef.current = true;
            return;
          }

          // 4. Move & Eat Food
          const curFood = foodRef.current;
          if (nextHead.x === curFood.x && nextHead.y === curFood.y) {
            const newSnake = [nextHead, ...prevSnake];
            snakeRef.current = newSnake;

            const newScore = curScore + 10;
            scoreRef.current = newScore;
            setScore(newScore);

            const storedHigh = parseInt(localStorage.getItem('love_snake_highscore') || '0', 10);
            if (newScore > storedHigh) {
              setHighScore(newScore);
              localStorage.setItem('love_snake_highscore', newScore.toString());
            }

            foodRef.current = getRandomFood(newSnake);
          } else {
            const newSnake = [nextHead, ...prevSnake.slice(0, -1)];
            snakeRef.current = newSnake;
          }
        }
      }

      // ── RENDER 60FPS SMOOTH CANVAS ──
      const w = canvas.width;
      const h = canvas.height;
      const cellW = w / GRID_SIZE;
      const cellH = h / GRID_SIZE;

      // Dark background
      ctx.fillStyle = '#090104';
      ctx.fillRect(0, 0, w, h);

      // Subtle grid dots/lines
      ctx.strokeStyle = 'rgba(226, 232, 240, 0.04)';
      ctx.lineWidth = 1;
      for (let i = 1; i < GRID_SIZE; i++) {
        ctx.beginPath();
        ctx.moveTo(i * cellW, 0);
        ctx.lineTo(i * cellW, h);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, i * cellH);
        ctx.lineTo(w, i * cellH);
        ctx.stroke();
      }

      // Draw Food (Pulsing Heart)
      const food = foodRef.current;
      const foodCenterX = food.x * cellW + cellW / 2;
      const foodCenterY = food.y * cellH + cellH / 2;
      const pulse = 1 + Math.sin(time * 0.007) * 0.12;

      ctx.save();
      ctx.translate(foodCenterX, foodCenterY);
      ctx.scale(pulse, pulse);
      ctx.font = `${cellW * 0.88}px "Segoe UI Emoji", "Apple Color Emoji", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(225, 29, 72, 0.85)';
      ctx.shadowBlur = 12;
      ctx.fillText('❤️', 0, 0);
      ctx.restore();

      // Draw Snake Body
      const snake = snakeRef.current;
      const pad = 1.5;

      for (let i = snake.length - 1; i > 0; i--) {
        const seg = snake[i];
        const segX = seg.x * cellW + pad;
        const segY = seg.y * cellH + pad;
        const segW = cellW - pad * 2;
        const segH = cellH - pad * 2;

        ctx.save();
        // Burgundy gradient with subtle rose highlight
        const grad = ctx.createLinearGradient(segX, segY, segX + segW, segY + segH);
        grad.addColorStop(0, '#e11d48');
        grad.addColorStop(1, '#881337');
        ctx.fillStyle = grad;
        ctx.shadowColor = 'rgba(225, 29, 72, 0.35)';
        ctx.shadowBlur = 6;
        ctx.beginPath();
        drawRoundedRect(ctx, segX, segY, segW, segH, 4);
        ctx.fill();
        ctx.restore();
      }

      // Draw Snake Head (Gothic Silver with expressive eyes)
      if (snake.length > 0) {
        const head = snake[0];
        const headX = head.x * cellW + pad;
        const headY = head.y * cellH + pad;
        const headW = cellW - pad * 2;
        const headH = cellH - pad * 2;

        ctx.save();
        ctx.fillStyle = '#f8fafc';
        ctx.shadowColor = 'rgba(248, 250, 252, 0.7)';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        drawRoundedRect(ctx, headX, headY, headW, headH, 5);
        ctx.fill();

        // Cute directional eyes
        const curDir = dirRef.current;
        ctx.fillStyle = '#0f172a';
        ctx.shadowBlur = 0;
        const eyeRadius = Math.max(1.5, cellW * 0.1);

        let eye1X, eye1Y, eye2X, eye2Y;
        if (curDir.x === 1) { // Moving Right
          eye1X = headX + headW * 0.75; eye1Y = headY + headH * 0.28;
          eye2X = headX + headW * 0.75; eye2Y = headY + headH * 0.72;
        } else if (curDir.x === -1) { // Moving Left
          eye1X = headX + headW * 0.25; eye1Y = headY + headH * 0.28;
          eye2X = headX + headW * 0.25; eye2Y = headY + headH * 0.72;
        } else if (curDir.y === 1) { // Moving Down
          eye1X = headX + headW * 0.28; eye1Y = headY + headH * 0.75;
          eye2X = headX + headW * 0.72; eye2Y = headY + headH * 0.75;
        } else { // Moving Up
          eye1X = headX + headW * 0.28; eye1Y = headY + headH * 0.25;
          eye2X = headX + headW * 0.72; eye2Y = headY + headH * 0.25;
        }

        ctx.beginPath();
        ctx.arc(eye1X, eye1Y, eyeRadius, 0, Math.PI * 2);
        ctx.arc(eye2X, eye2Y, eyeRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    };

    animId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animId);
  }, [getRandomFood]);

  // Touch Swipe Gesture on Canvas
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e) => {
    if (isGameOverRef.current) {
      resetGame();
      return;
    }
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const minSwipe = 20;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (Math.abs(dx) > minSwipe) {
        if (dx > 0) queueDirection({ x: 1, y: 0 });
        else queueDirection({ x: -1, y: 0 });
      }
    } else {
      if (Math.abs(dy) > minSwipe) {
        if (dy > 0) queueDirection({ x: 0, y: 1 });
        else queueDirection({ x: 0, y: -1 });
      }
    }
  };

  const handleDpad = (newDir) => {
    if (isGameOverRef.current) {
      resetGame();
      return;
    }
    queueDirection(newDir);
  };

  return (
    <div className="retro-snake-container">
      {/* Score Header */}
      <div className="retro-snake-header">
        <div className="retro-snake-stat">
          <span className="retro-stat-label">SKOR</span>
          <span className="retro-stat-value">{score}</span>
        </div>
        <div className="retro-snake-actions">
          <button
            className="retro-control-btn"
            onClick={() => setIsPaused((p) => !p)}
            type="button"
          >
            {isPaused ? 'Devam' : 'Duraklat'}
          </button>
          <button
            className="retro-control-btn"
            onClick={resetGame}
            type="button"
          >
            Yeniden
          </button>
        </div>
        <div className="retro-snake-stat">
          <span className="retro-stat-label">REKOR</span>
          <span className="retro-stat-value">{highScore}</span>
        </div>
      </div>

      {/* Board with HTML5 Canvas (60FPS Buttery Smooth) */}
      <div
        className="retro-board-wrapper"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <canvas
          ref={canvasRef}
          className="retro-snake-canvas"
          width={480}
          height={480}
        />

        {/* Game Over Overlay */}
        {isGameOver && (
          <div className="retro-game-over-overlay">
            <h4 className="game-over-title">OYUN BİTTİ</h4>
            <p className="game-over-score">Skorun: {score}</p>
            <button className="game-over-restart-btn" onClick={resetGame}>
              Tekrar Oyna
            </button>
          </div>
        )}

        {/* Pause Overlay */}
        {isPaused && !isGameOver && (
          <div className="retro-game-over-overlay">
            <h4 className="game-over-title">DURAKLATILDI</h4>
            <button
              className="game-over-restart-btn"
              onClick={() => setIsPaused(false)}
            >
              Devam Et
            </button>
          </div>
        )}
      </div>

      {/* Touch D-Pad for Mobile with 0ms Instant PointerDown */}
      <div className="retro-dpad" aria-label="Mobil Yön Tuşları">
        <div className="dpad-row dpad-center">
          <button
            className="dpad-btn dpad-up"
            onPointerDown={(e) => { e.preventDefault(); handleDpad({ x: 0, y: -1 }); }}
            aria-label="Yukarı"
            type="button"
          >
            ▲
          </button>
        </div>
        <div className="dpad-row dpad-middle">
          <button
            className="dpad-btn dpad-left"
            onPointerDown={(e) => { e.preventDefault(); handleDpad({ x: -1, y: 0 }); }}
            aria-label="Sol"
            type="button"
          >
            ◀
          </button>
          <div className="dpad-center-hub" />
          <button
            className="dpad-btn dpad-right"
            onPointerDown={(e) => { e.preventDefault(); handleDpad({ x: 1, y: 0 }); }}
            aria-label="Sağ"
            type="button"
          >
            ▶
          </button>
        </div>
        <div className="dpad-row dpad-center">
          <button
            className="dpad-btn dpad-down"
            onPointerDown={(e) => { e.preventDefault(); handleDpad({ x: 0, y: 1 }); }}
            aria-label="Aşağı"
            type="button"
          >
            ▼
          </button>
        </div>
      </div>
    </div>
  );
};

export default RetroLoveSnake;
